import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Grid, Card, Typography, Switch, IconButton,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { USERS_ENDPOINT } from '../../config/urls';
import { useAuth } from '../../hooks/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { formatWithTzLabel } from "../../utils/dateUtils";
import ButtonOne from '../../common/normal-button';
import { ColorsConstants } from '../../cssUtils/constants';
import typography from '../../cssUtils/typography';
import AppChip from '../../common/app-chip';
import TableSearchInput from '../TableSearchInput';
import CustomPagination from '../../common/custom-pagination';
import {
  resolveSearchQueryForApi,
  normalizeCombinedSearchInput,
} from '../../utils/phoneSearch';
function UsersTable({ props }) {
  const { getValidToken } = useAuth();
  const auth = useAuth();
  const navigate = useNavigate();

  const [usersData, setUsersData] = useState([]);
  const [addUserFlag, setAddUserFlag] = useState(false);
  const [refreshKey] = useState(0);
  const [localSearch, setLocalSearch] = useState('');
  const [showActive, setShowActive] = useState(true);

  // ── Client-side pagination state ──
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(9);

  const permissions = localStorage.getItem('permissions') || [];
  const hasAccessPermission = permissions.includes('users.add');

  const resolvedUserSearch = useMemo(
    () => resolveSearchQueryForApi(localSearch),
    [localSearch],
  );

  // ── Server-side search (search_query); list filtered by active toggle on client ──
  useEffect(() => {
    if (resolvedUserSearch.blockFetch) {
      return;
    }
    const fetchData = async () => {
      try {
        const token = await getValidToken();
        const tenant = localStorage.getItem('tenant');
        const headers = {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Tenant-Id': tenant,
        };

        const params = new URLSearchParams();
        if (resolvedUserSearch.apiSearchQuery) {
          params.set('search_query', resolvedUserSearch.apiSearchQuery);
        }

        const url = params.toString()
          ? `${USERS_ENDPOINT}?${params.toString()}`
          : USERS_ENDPOINT;

        const response = await fetch(url, { headers });

        if (!response.ok) {
          const errorData = await response.json();
          if (errorData.code === 'user_inactive') {
            auth.logOut();
            navigate('/login');
          }
          throw new Error('Failed to fetch data');
        }

        const jsonData = await response.json();
        setUsersData(jsonData.results);
        setAddUserFlag(jsonData.add_user_flag);
      } catch (error) {
        console.error('Error fetching data:', error.message);
      }
    };

    fetchData();
  }, [auth, getValidToken, navigate, refreshKey, resolvedUserSearch.apiSearchQuery, resolvedUserSearch.blockFetch]);

  // ── Handlers ──
  const handlePageChange = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (value) => {
    setLocalSearch(normalizeCombinedSearchInput(value));
    setPage(0); // reset to first page on search
  };

  const handleToggleActive = (e) => {
    setShowActive(e.target.checked);
    setPage(0); // reset to first page on toggle
  };

  const handleEditClick = (user) => {
    if (props?.onNavigate) {
      props.onNavigate('users_add', { mode: 'edit', userId: user.id, userData: user });
    }
  };

  const handleAddClick = () => {
    if (props?.onNavigate) {
      props.onNavigate('users_add', { mode: 'add' });
    }
  };

  // ── Client-side active/inactive only (search handled by API) ──
  const filteredUsers = usersData.filter((user) => {
    const matchesActive = showActive ? user.is_active : !user.is_active;
    return matchesActive;
  });

  // ── Client-side slice for current page ──
  const paginatedUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const roleColor = (role) => {
    const r = (role || '').toLowerCase();
    if (r === 'admin') return { bg: '#F59E0B', color: '#fff' };
    return { bg: ColorsConstants.primaryBackgroundColor, color: '#fff' };
  };

  return (
    <Box sx={{ backgroundColor: '#FAFAFA', minHeight: '100vh' }}>
      {/* ── Top Header Bar ── */}
      <Box
        sx={{
          backgroundColor: '#fff',
          borderBottom: '1px solid #E5E7EB',
          px: 3, py: 1.75,
          display: 'flex', alignItems: 'center', gap: 2,
        }}
      >
        {/* Inactive / Active Toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Typography sx={{ ...typography.bodySmall, fontWeight: showActive ? 400 : 600, color: showActive ? '#6C7C93' : '#04142F' }}>
            Inactive
          </Typography>
          <Switch
            checked={showActive}
            onChange={handleToggleActive}
            size="small"
            sx={{
              width: 36, height: 20, padding: 0,
              '& .MuiSwitch-switchBase': {
                padding: 0, margin: '2px',
                '&.Mui-checked': {
                  transform: 'translateX(16px)', color: '#fff',
                  '& + .MuiSwitch-track': { backgroundColor: ColorsConstants.primaryBackgroundColor, opacity: 1 },
                },
              },
              '& .MuiSwitch-thumb': { width: 16, height: 16, boxShadow: 'none' },
              '& .MuiSwitch-track': { borderRadius: 10, backgroundColor: '#C8D0E0', opacity: 1 },
            }}
          />
          <Typography sx={{ ...typography.bodySmall, fontWeight: showActive ? 600 : 400, color: showActive ? '#04142F' : '#6C7C93' }}>
            Active
          </Typography>
        </Box>

        {/* Search */}
        <Box sx={{ flex: 1 }}>
          <TableSearchInput
            searchQuery={localSearch}
            onSearchChange={(e) => handleSearchChange(e.target.value)}
            tooltip="Search by Name, Phone Number, Email"
            placeholder="Search by Name, Phone Number, Email"
            error={Boolean(resolvedUserSearch.blockFetch && resolvedUserSearch.phoneSearchError)}
            helperText={resolvedUserSearch.blockFetch ? resolvedUserSearch.phoneSearchError : undefined}
          />
        </Box>

        {/* Add New User */}
        {hasAccessPermission && (
          <ButtonOne
            variant="contained"
            onClick={handleAddClick}
            disabled={addUserFlag === false}
            sx={{ whiteSpace: 'nowrap', height: 40, px: 2.5, ...typography.bodyLarge }}
          >
            Add New User
          </ButtonOne>
        )}
      </Box>

      {/* ── Cards Grid + Pagination ── */}
      <Box sx={{ p: 3, bgcolor: "#fafafa" }}>
        {paginatedUsers.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography sx={{ ...typography.bodyLarge, color: '#6C7C93' }}>No users found</Typography>
          </Box>
        ) : (
          <Grid container spacing={2.5}>
            {paginatedUsers.map((user, index) => {
              const rc = roleColor(user.role);
              return (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: '12px', border: '1px solid #E5E7EB',
                      backgroundColor: '#fff', overflow: 'hidden',
                      transition: 'box-shadow 0.2s ease',
                      '&:hover': { boxShadow: '0px 4px 12px rgba(0,0,0,0.08)' },
                    }}
                  >
                    {/* Card Header */}
                    <Box sx={{ backgroundColor: '#F0F5FF', px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography sx={{ ...typography.bodyLarge, fontWeight: 700, color: ColorsConstants.primaryBackgroundColor }}>
                        {user.name || '—'}
                      </Typography>
                      {hasAccessPermission && (
                        <IconButton size="small" onClick={() => handleEditClick(user)} sx={{ color: '#6C7C93', '&:hover': { color: ColorsConstants.primaryBackgroundColor } }}>
                          <EditOutlinedIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      )}
                    </Box>

                    {/* Card Body */}
                    <Box sx={{ px: 2, pt: 2, pb: 2 }}>
                      <Typography sx={{ ...typography.bodySmall, color: '#6C7C93', mb: 0.25 }}>Email</Typography>
                      <Typography sx={{ ...typography.bodyLarge, color: '#04142F', mb: 1.75 }}>{user.email || '—'}</Typography>

                      <Typography sx={{ ...typography.bodySmall, color: '#6C7C93', mb: 0.25 }}>Phone number</Typography>
                      <Typography sx={{ ...typography.bodyLarge, color: '#04142F', mb: 1.75 }}>
                        {user.country_code || ''}{user.dial_code || '—'}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', mt: 0.5 }}>
                        <Box>
                          <Typography sx={{ ...typography.bodySmall, color: '#6C7C93', mb: 0.25 }}>Created At</Typography>
                          <Typography sx={{ ...typography.bodyLarge, color: '#04142F' }}>
                            {user.created_at ? formatWithTzLabel(user.created_at) : '—'}
                          </Typography>
                        </Box>
                        <AppChip
                          label={user.role || 'USER'}
                          size="small"
                          variant="filled"
                          sx={{
                            backgroundColor: rc.bg, color: rc.color,
                            fontWeight: 700, letterSpacing: '0.3px',
                            textTransform: 'uppercase', height: 24,
                            ...typography.bodySmall,
                          }}
                        />
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* ── Client-side Pagination (inside content wrapper to stay in viewport) ── */}
        {filteredUsers.length > 9 && (
          <Box sx={{ borderTop: '1px solid #E5E7EB', backgroundColor: '#fff', mt: 2, borderRadius: '12px' }}>
            <CustomPagination
              count={filteredUsers.length}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[9, 18, 36]}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default UsersTable;